import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { APP_CONFIG, STORAGE_KEYS, ERROR_CODES } from '@/utils/constants';
import { ApiResponse } from '@/types';
import type {
  BankAccountDTO,
  BankCapabilities,
  Saldo,
  Extrato,
  PixRecebido,
  Cobranca,
  EnvioPix,
} from '@/types/bank';

/**
 * Chave do redux-persist no localStorage (`persist:` + PERSIST_KEY de store/index.ts).
 * Literal aqui porque store → authSlice → api criaria import circular.
 */
const REDUX_PERSIST_STORAGE_KEY = 'persist:falavippay-root';

// =====================================================
// CONFIGURAÇÃO DA API
// =====================================================

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  /** Override em memória (setado ao trocar de conta) — vence o estado persistido até o reload */
  private bankAccountIdOverride: string | null | undefined = undefined;

  constructor() {
    this.baseURL = APP_CONFIG.API_URL;
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - adiciona token de autenticação
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Adiciona company_id se disponível
        const companyData = this.getCompanyData();
        if (companyData?.id) {
          config.headers['X-Company-Id'] = companyData.id;
        }

        // Adiciona a conta bancária selecionada (empresa · banco), se houver
        const bankAccountId = this.getBankAccountId();
        if (bankAccountId) {
          config.headers['X-Bank-Account-Id'] = bankAccountId;
        }

        // Log da requisição em desenvolvimento
        if (import.meta.env.DEV) {
          console.log('🚀 API Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            params: config.params,
            data: config.data,
          });
        }

        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - trata respostas e erros
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        // Log da resposta em desenvolvimento
        if (import.meta.env.DEV) {
          console.log('✅ API Response:', {
            status: response.status,
            url: response.config.url,
            data: response.data,
          });
        }

        return response;
      },
      async (error: AxiosError<ApiResponse>) => {
        console.error('❌ API Error:', error);

        // Tenta refresh do token se 401
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
          const refreshed = await this.refreshToken();
          
          if (refreshed && error.config) {
            // Retry a requisição original
            return this.api.request(error.config);
          } else {
            // Redirect para login
            this.handleAuthError();
          }
        }

        // Transforma erro em formato padrão
        const apiError = this.transformError(error);
        return Promise.reject(apiError);
      }
    );
  }

  private transformError(error: AxiosError<ApiResponse>) {
    if (error.response?.data) {
      return error.response.data;
    }

    if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        error: {
          code: ERROR_CODES.TIMEOUT,
          message: 'Tempo limite da requisição excedido',
        },
      };
    }

    if (error.code === 'NETWORK_ERROR' || !error.response) {
      return {
        success: false,
        error: {
          code: ERROR_CODES.NETWORK_ERROR,
          message: 'Erro de conexão com o servidor',
        },
      };
    }

    return {
      success: false,
      error: {
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error.message || 'Erro interno do servidor',
      },
    };
  }

  private getAuthToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  private getCompanyData() {
    const data = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Lê o id da conta bancária selecionada do estado persistido pelo redux-persist
   * (slice `bankAccount`, whitelist em store/index.ts). O redux-persist guarda cada
   * slice como string JSON dentro do objeto raiz — por isso o parse em dois níveis.
   */
  private getBankAccountId(): string | null {
    if (this.bankAccountIdOverride !== undefined) {
      return this.bankAccountIdOverride;
    }
    try {
      const root = localStorage.getItem(REDUX_PERSIST_STORAGE_KEY);
      if (!root) return null;
      const parsed = JSON.parse(root);
      const sliceRaw = parsed?.bankAccount;
      if (!sliceRaw) return null;
      const slice = typeof sliceRaw === 'string' ? JSON.parse(sliceRaw) : sliceRaw;
      return slice?.selected?.id || null;
    } catch {
      return null;
    }
  }

  /**
   * Define em memória a conta que vai no header X-Bank-Account-Id (chamar junto com
   * dispatch(selectAccount) — o redux-persist grava de forma assíncrona, e assim a
   * próxima requisição já sai com a conta certa). `null` = não mandar o header.
   */
  setBankAccountId(id: string | null) {
    this.bankAccountIdOverride = id;
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) return false;

      const response = await axios.post(`${this.baseURL}/auth/refresh`, {
        refreshToken,
      });

      if (response.data.success) {
        const { token, refreshToken: newRefreshToken } = response.data.data;
        
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        if (newRefreshToken) {
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      return false;
    }
  }

  private handleAuthError() {
    // Limpa dados de autenticação
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);

    // Redirect para login (será implementado com React Router)
    window.location.href = '/login';
  }

  // =====================================================
  // MÉTODOS PÚBLICOS
  // =====================================================

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // Upload de arquivos
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await this.api.post<ApiResponse<T>>(url, formData, config);
    return response.data;
  }

  // Download de arquivos
  async download(url: string, filename?: string, config?: AxiosRequestConfig): Promise<void> {
    const response = await this.api.get(url, {
      ...config,
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Configurar token manualmente
  setAuthToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  // Remover token
  clearAuth() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  // Obter instância do axios para casos especiais
  getInstance(): AxiosInstance {
    return this.api;
  }
}

// =====================================================
// INSTÂNCIA SINGLETON
// =====================================================

export const apiService = new ApiService();

// =====================================================
// ENDPOINTS ESPECÍFICOS
// =====================================================

// Auth endpoints
export const authApi = {
  login: (email: string, password: string) =>
    apiService.post('/auth/login', { email, password }),
  
  register: (userData: any) =>
    apiService.post('/auth/register', userData),
  
  logout: () =>
    apiService.post('/auth/logout'),
  
  refreshToken: (refreshToken: string) =>
    apiService.post('/auth/refresh', { refreshToken }),
  
  forgotPassword: (email: string) =>
    apiService.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    apiService.post('/auth/reset-password', { token, password }),
  
  me: () =>
    apiService.get('/auth/me'),
  
  enable2FA: () =>
    apiService.post('/auth/2fa/enable'),
  
  verify2FA: (code: string, twoFactorToken: string) =>
    apiService.post('/auth/2fa/verify', { code, twoFactorToken }),

  disable2FA: (code: string) =>
    apiService.post('/auth/2fa/disable', { code }),

  // Google OAuth
  googleLogin: (credential: string) =>
    apiService.post('/auth/google', { credential }),

  // Agent Token
  generateAgentToken: (data: { companyId: string; expiresIn: string }) =>
    apiService.post('/auth/agent-token', data),
};

// PIX endpoints
export const pixApi = {
  // Cobranças
  createCharge: (data: any) =>
    apiService.post('/pix/charge', data),
  
  getCharge: (txid: string) =>
    apiService.get(`/pix/charge/${txid}`),
  
  listCharges: (params?: any) =>
    apiService.get('/pix/charges', { params }),
  
  cancelCharge: (txid: string) =>
    apiService.delete(`/pix/charge/${txid}`),
  
  // Pagamentos
  createPayment: (data: any) =>
    apiService.post('/pix/payment', data),
  
  getPayment: (e2eid: string) =>
    apiService.get(`/pix/payment/${e2eid}`),
  
  listPayments: (params?: any) =>
    apiService.get('/pix/payments', { params }),
  
  // Transações
  listTransactions: (params?: any) =>
    apiService.get('/pix/transactions', { params }),
  
  getTransaction: (id: string) =>
    apiService.get(`/pix/transactions/${id}`),
  
  // QR Code
  generateQRCode: (data: any) =>
    apiService.post('/pix/qrcode', data),
  
  decodeQRCode: (qrcode: string) =>
    apiService.post('/pix/qrcode/decode', { qrcode }),
  
  // Chaves PIX
  listPixKeys: () =>
    apiService.get('/pix/keys'),
  
  createPixKey: (data: any) =>
    apiService.post('/pix/keys', data),
  
  deletePixKey: (key: string) =>
    apiService.delete(`/pix/keys/${encodeURIComponent(key)}`),
};

// Solicitações de Pagamento (de apps integrados)
export const paymentRequestsApi = {
  list: (params?: any) =>
    apiService.get('/payment-requests', { params }),

  get: (id: string) =>
    apiService.get(`/payment-requests/${id}`),

  approve: (id: string) =>
    apiService.post(`/payment-requests/${id}/approve`),

  reject: (id: string, reason?: string) =>
    apiService.post(`/payment-requests/${id}/reject`, { reason }),

  comprovante: (id: string) =>
    apiService.get(`/payment-requests/${id}/comprovante`),

  // Envia o comprovante no WhatsApp. `to` em branco usa o destino configurado (grupo).
  sendComprovanteWhatsapp: (id: string, to?: string) =>
    apiService.post(`/payment-requests/${id}/comprovante/whatsapp`, { to }),
};

// Config de notificações + aprovação por grupo
// Links do painel do aprovador (/aprovar#token) — gestão pelo admin
export const approverLinksApi = {
  list: () => apiService.get('/approver-links'),
  create: (data: { name: string; whatsapp: string }) => apiService.post('/approver-links', data),
  revoke: (id: string) => apiService.post(`/approver-links/${id}/revoke`),
};

export const notificationConfigApi = {
  get: () => apiService.get('/notification-config'),
  update: (data: any) => apiService.put('/notification-config', data),
};

// Boletos endpoints
export const boletosApi = {
  create: (data: any) =>
    apiService.post('/bankslips', data),
  
  get: (id: string) =>
    apiService.get(`/bankslips/${id}`),
  
  list: (params?: any) =>
    apiService.get('/bankslips', { params }),
  
  cancel: (id: string) =>
    apiService.put(`/bankslips/${id}/cancel`),
  
  downloadPDF: (id: string) =>
    apiService.download(`/bankslips/${id}/pdf`, `boleto-${id}.pdf`),
  
  sendEmail: (id: string, emails: string[]) =>
    apiService.post(`/bankslips/${id}/send-email`, { emails }),
  
  updateDueDate: (id: string, dueDate: string) =>
    apiService.patch(`/bankslips/${id}/due-date`, { dueDate }),
};

// Transferências endpoints
export const transfersApi = {
  create: (data: any) =>
    apiService.post('/transfers', data),
  
  get: (id: string) =>
    apiService.get(`/transfers/${id}`),
  
  list: (params?: any) =>
    apiService.get('/transfers', { params }),
  
  cancel: (id: string) =>
    apiService.put(`/transfers/${id}/cancel`),
  
  // TED
  createTED: (data: any) =>
    apiService.post('/account/transfer', data),
  
  // Transferência interna
  createInternal: (data: any) =>
    apiService.post('/transfers/internal', data),
  
  // Agendamentos
  listScheduled: (params?: any) =>
    apiService.get('/transfers/scheduled', { params }),
  
  cancelScheduled: (id: string) =>
    apiService.delete(`/transfers/scheduled/${id}`),
};

// Conta endpoints
export const accountApi = {
  getBalance: () =>
    apiService.get('/account/balance'),
  
  getStatement: (params?: any) =>
    apiService.get('/account/statement', { params }),
  
  exportStatement: (params: any) =>
    apiService.get('/account/statement/export', { params }),
};

// Relatórios endpoints
export const reportsApi = {
  getTransactions: (params?: any) =>
    apiService.get('/reports/transactions', { params }),
  
  getRevenue: (params?: any) =>
    apiService.get('/reports/revenue', { params }),
  
  getDashboard: (params?: any) =>
    apiService.get('/reports/dashboard', { params }),
  
  exportTransactions: (params: any, format: 'pdf' | 'excel' | 'csv' = 'excel') =>
    apiService.download(`/reports/export/${format}`, `relatorio-transacoes.${format}`, { params }),
};

// Usuários endpoints
export const usersApi = {
  list: (params?: any) =>
    apiService.get('/users', { params }),
  
  get: (id: string) =>
    apiService.get(`/users/${id}`),
  
  create: (data: any) =>
    apiService.post('/users', data),
  
  update: (id: string, data: any) =>
    apiService.put(`/users/${id}`, data),
  
  delete: (id: string) =>
    apiService.delete(`/users/${id}`),
  
  updatePassword: (id: string, data: any) =>
    apiService.put(`/users/${id}/password`, data),
  
  updateProfile: (data: any) =>
    apiService.put('/users/profile', data),
  
  uploadAvatar: (file: File, onProgress?: (progress: number) => void) =>
    apiService.upload('/users/avatar', file, onProgress),
};

// Empresas endpoints
export const companiesApi = {
  list: (params?: any) =>
    apiService.get('/companies', { params }),
  
  get: (id: string) =>
    apiService.get(`/companies/${id}`),
  
  create: (data: any) =>
    apiService.post('/companies', data),
  
  update: (id: string, data: any) =>
    apiService.put(`/companies/${id}`, data),
  
  delete: (id: string) =>
    apiService.delete(`/companies/${id}`),
  
  updateSettings: (id: string, data: any) =>
    apiService.put(`/companies/${id}/settings`, data),
};

// Notificações endpoints
export const notificationsApi = {
  list: (params?: any) =>
    apiService.get('/notifications', { params }),

  markAsRead: (id: string) =>
    apiService.put(`/notifications/${id}/read`),

  markAllAsRead: () =>
    apiService.put('/notifications/mark-all-read'),

  getUnreadCount: () =>
    apiService.get('/notifications/unread-count'),
};

// Certificados endpoints (Sicoob)
export const certificatesApi = {
  list: (params?: any) =>
    apiService.get('/certificates', { params }),

  get: (id: string) =>
    apiService.get(`/certificates/${id}`),

  upload: (data: { alias: string; description?: string; certificate: string; password: string }) =>
    apiService.post('/certificates', data),

  update: (id: string, data: { alias?: string; description?: string; status?: string }) =>
    apiService.patch(`/certificates/${id}`, data),

  delete: (id: string) =>
    apiService.delete(`/certificates/${id}`),

  getStatus: () =>
    apiService.get('/certificates/status'),

  testConnection: (clientId?: string) =>
    apiService.post('/certificates/test-connection', { clientId }),

  convertPfx: (data: { certificate: string; password: string }) =>
    apiService.post('/certificates/convert', data),
};

// Apps integrados endpoints
export const appsApi = {
  list: (params?: any) =>
    apiService.get('/apps', { params }),

  get: (id: string) =>
    apiService.get(`/apps/${id}`),

  create: (data: { name: string; description?: string; webhook_url: string }) =>
    apiService.post('/apps', data),

  update: (id: string, data: any) =>
    apiService.put(`/apps/${id}`, data),

  delete: (id: string) =>
    apiService.delete(`/apps/${id}`),

  regenerateKey: (id: string) =>
    apiService.post(`/apps/${id}/regenerate-key`),

  getCharges: (id: string, params?: any) =>
    apiService.get(`/apps/${id}/charges`, { params }),

  getWebhookLogs: (id: string, params?: any) =>
    apiService.get(`/apps/${id}/webhook-logs`, { params }),
};

// Efí (API Pix) endpoints
export const efiApi = {
  getConfig: () => apiService.get('/efi/config'),
  saveConfig: (data: {
    environment: string; clientId: string; clientSecret?: string; pixKey?: string;
    webhookSkipMtls?: boolean; webhookCheckIp?: boolean;
  }) => apiService.post('/efi/config', data),
  uploadCertificate: (data: { certificate: string; password?: string }) =>
    apiService.post('/efi/certificate', data),
  deleteCertificate: () => apiService.delete('/efi/certificate'),
  testConnection: () => apiService.post('/efi/test-connection'),

  getWebhook: () => apiService.get('/efi/webhook'),
  registerWebhook: (chave?: string) => apiService.post('/efi/webhook/register', { chave }),
  unregisterWebhook: () => apiService.delete('/efi/webhook'),
  regenerateWebhook: () => apiService.post('/efi/webhook/regenerate'),
  webhookLogs: (limit = 50) => apiService.get('/efi/webhook/logs', { params: { limit } }),

  saldo: () => apiService.get('/efi/saldo'),
  keys: () => apiService.get('/efi/keys'),

  createCob: (data: any) => apiService.post('/efi/cob', data),
  listCobs: (params: any) => apiService.get('/efi/cob', { params }),
  cancelCob: (txid: string) => apiService.delete(`/efi/cob/${txid}`),

  pixRecebidos: (params: any) => apiService.get('/efi/pix/recebidos', { params }),
  pixEnviados: (params: any) => apiService.get('/efi/pix/enviados', { params }),
  enviarPix: (data: any) => apiService.post('/efi/pix/enviar', data),
  syncPix: (data: { inicio?: string; fim?: string }) => apiService.post('/efi/pix/sync', data),
  historico: (params?: any) => apiService.get('/efi/pix/historico', { params }),

  relatorios: () => apiService.get('/efi/relatorios'),
  solicitarRelatorio: (dataMovimento: string) => apiService.post('/efi/relatorios', { dataMovimento }),
  buscarRelatorio: (id: string) => apiService.post(`/efi/relatorios/${id}/fetch`),
  csvUrl: (id: string) => `/efi/relatorios/${id}/csv`,
  registros: (params: any) => apiService.get('/efi/conciliacao/registros', { params }),

  // API Cobranças — venda por link (cartão / boleto)
  cobrancas: (params?: { status?: string; refresh?: boolean; limit?: number }) => apiService.get('/efi/cobrancas', { params }),
  criarCobrancaLink: (data: {
    valor: string | number; descricao: string; paymentMethod: 'credit_card' | 'banking_billet' | 'all';
    validadeDias?: number; mensagem?: string; cliente?: { nome?: string; documento?: string; email?: string; telefone?: string };
  }) => apiService.post('/efi/cobrancas', data),
  cobranca: (chargeId: number | string) => apiService.get(`/efi/cobrancas/${chargeId}`),
  cancelarCobranca: (chargeId: number | string) => apiService.post(`/efi/cobrancas/${chargeId}/cancelar`),
  reapontarNotificacao: (chargeId: number | string) => apiService.post(`/efi/cobrancas/${chargeId}/notification-url`),
};

// =====================================================
// CAMADA NEUTRA DE BANCO (/bank/*) — ver docs/CONTAS_BANCARIAS.md
// Todas as rotas abaixo de /bank/current usam a conta do header
// X-Bank-Account-Id (mandado automaticamente pelo interceptor).
// =====================================================

export interface CriarCobrancaInput {
  valor: number;
  descricao?: string;
  devedor?: { nome: string; documento: string };
  expiracaoSegundos?: number;
}

export interface EnviarPixInput {
  valor: number;
  chave: string;
  descricao?: string;
  idempotencyKey?: string;
}

export const bankApi = {
  /** Contas da empresa atual (X-Company-Id) */
  accounts: () =>
    apiService.get<BankAccountDTO[]>('/bank/accounts'),

  /** Contas de todas as empresas do usuário (com companyName) — usado pelo seletor */
  accountsAll: () =>
    apiService.get<BankAccountDTO[]>('/bank/accounts/all'),

  /** Grava users.selected_bank_account_id (e selected_company_id) */
  select: (bankAccountId: string) =>
    apiService.put<BankAccountDTO>('/bank/accounts/select', { bankAccountId }),

  current: () =>
    apiService.get<BankAccountDTO & { capabilities: BankCapabilities }>('/bank/current'),

  capabilities: () =>
    apiService.get<BankCapabilities>('/bank/capabilities'),

  saldo: () =>
    apiService.get<Saldo>('/bank/saldo'),

  /** inicio/fim em YYYY-MM-DD (máx. 12 meses) */
  extrato: (inicio: string, fim: string) =>
    apiService.get<Extrato>('/bank/extrato', { params: { inicio, fim } }),

  pixRecebidos: (inicio: string, fim: string) =>
    apiService.get<{ itens: PixRecebido[]; total: number }>('/bank/pix/recebidos', { params: { inicio, fim } }),

  criarCobranca: (data: CriarCobrancaInput) =>
    apiService.post<Cobranca>('/bank/pix/cobranca', data),

  getCobranca: (txid: string) =>
    apiService.get<Cobranca>(`/bank/pix/cobranca/${encodeURIComponent(txid)}`),

  /** Requer admin/master */
  enviarPix: (data: EnviarPixInput) =>
    apiService.post<EnvioPix>('/bank/pix/enviar', data),

  pixEnviados: (inicio: string, fim: string) =>
    apiService.get<{ itens: EnvioPix[] }>('/bank/pix/enviados', { params: { inicio, fim } }),
};

export default apiService;

// Export named para compatibilidade
export const api = apiService;